/**
 * UploadArea V2
 *
 * Drag & Drop Upload-Bereich für Bilder mit:
 * - Drag & Drop Support
 * - Click to Upload
 * - Multi-File Support (konfigurierbar)
 * - File Size Validation
 * - Preview Grid mit Thumbnails
 * - Remove Button pro Datei
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Plus, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, Alert, AlertDescription } from '@/components/v2/ui';

interface UploadAreaProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFormats?: string[];
  disabled?: boolean;
  className?: string;
}

const MAX_FILES_DEFAULT = 10;
const MAX_FILE_SIZE_DEFAULT = 6 * 1024 * 1024; // 6MB

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function UploadArea({
  files,
  onFilesChange,
  maxFiles = MAX_FILES_DEFAULT,
  maxFileSize = MAX_FILE_SIZE_DEFAULT,
  acceptedFormats = ['image/*'],
  disabled = false,
  className,
}: UploadAreaProps) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate unique key for file
  const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

  // Handle file selection
  const handleFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const file of fileArray) {
        // Check if image
        if (!file.type.startsWith('image/')) {
          errors.push(`"${file.name}" ist kein Bild`);
          continue;
        }

        // Check size
        if (file.size > maxFileSize) {
          errors.push(`"${file.name}" ist zu groß (max. ${formatFileSize(maxFileSize)})`);
          continue;
        }

        // Check duplicates
        const key = getFileKey(file);
        if (files.some((f) => getFileKey(f) === key)) {
          continue;
        }

        validFiles.push(file);
      }

      // Check max files
      const totalFiles = files.length + validFiles.length;
      if (totalFiles > maxFiles) {
        const allowed = maxFiles - files.length;
        if (allowed > 0) {
          validFiles.splice(allowed);
          errors.push(`Nur ${maxFiles} Bilder erlaubt`);
        } else {
          errors.push(`Maximum von ${maxFiles} Bildern erreicht`);
        }
      }

      if (errors.length > 0) {
        setError(errors.join('. '));
        setTimeout(() => setError(null), 5000);
      }

      if (validFiles.length > 0) {
        const updatedFiles = [...files, ...validFiles];
        onFilesChange(updatedFiles);
      }
    },
    [files, maxFiles, maxFileSize, onFilesChange]
  );

  // Remove single file
  const removeFile = useCallback(
    (index: number) => {
      const newFiles = [...files];
      const removed = newFiles.splice(index, 1)[0];

      // Remove preview
      const key = getFileKey(removed);
      setPreviews((prev) => {
        const updated = new Map(prev);
        const url = updated.get(key);
        if (url) {
          URL.revokeObjectURL(url);
        }
        updated.delete(key);
        return updated;
      });

      onFilesChange(newFiles);
    },
    [files, onFilesChange]
  );

  // Clear all files
  const clearAllFiles = useCallback(() => {
    // Revoke all object URLs
    previews.forEach((url) => URL.revokeObjectURL(url));
    onFilesChange([]);
    setPreviews(new Map());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFilesChange, previews]);

  // Drag & Drop handlers
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!disabled && e.dataTransfer.files) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, handleFiles]
  );

  // Click handler
  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
      // Reset input value so same file can be selected again
      e.target.value = '';
    },
    [handleFiles]
  );

  // Generate previews for files
  useEffect(() => {
    files.forEach((file) => {
      const key = getFileKey(file);

      if (!previews.has(key)) {
        const url = URL.createObjectURL(file);
        setPreviews((prev) => new Map(prev).set(key, url));
      }
    });

    // Cleanup: remove previews for removed files
    const fileKeys = new Set(files.map(getFileKey));
    previews.forEach((url, key) => {
      if (!fileKeys.has(key)) {
        URL.revokeObjectURL(url);
        setPreviews((prev) => {
          const updated = new Map(prev);
          updated.delete(key);
          return updated;
        });
      }
    });
  }, [files, previews]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  // Empty state - show dropzone
  if (files.length === 0) {
    return (
      <div className={cn('space-y-3', className)}>
        {error && (
          <Alert variant="error" dismissible onDismiss={() => setError(null)}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative flex flex-col items-center justify-center p-8 rounded-xl border-2 border-dashed transition-all cursor-pointer',
            'min-h-[200px]',
            isDragOver
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
              : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-600',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Bilder hochladen"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            multiple={maxFiles > 1}
            className="hidden"
            onChange={handleInputChange}
            disabled={disabled}
          />

          <div
            className={cn(
              'flex items-center justify-center w-16 h-16 rounded-full mb-4',
              'bg-neutral-100 dark:bg-neutral-800',
              isDragOver && 'bg-primary-100 dark:bg-primary-900/30'
            )}
          >
            <Upload
              className={cn(
                'h-8 w-8',
                isDragOver
                  ? 'text-primary-500'
                  : 'text-neutral-400 dark:text-neutral-500'
              )}
            />
          </div>

          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
            Bilder hochladen
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
            {t('common.dragDropOrClick')}
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            Max. {maxFiles} Bild{maxFiles > 1 ? 'er' : ''}, je max.{' '}
            {formatFileSize(maxFileSize)}
          </p>
        </div>
      </div>
    );
  }

  // Preview grid with files
  return (
    <div className={cn('space-y-3', className)}>
      {error && (
        <Alert variant="error" dismissible onDismiss={() => setError(null)}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {files.length} Bild{files.length !== 1 ? 'er' : ''} ausgewählt
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFiles}
          disabled={disabled}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
        >
          <X className="h-4 w-4 mr-1" />
          Alle entfernen
        </Button>
      </div>

      {/* Preview Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {files.map((file, index) => {
          const key = getFileKey(file);
          const preview = previews.get(key);

          return (
            <div
              key={key}
              className="relative group aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800"
            >
              {preview ? (
                <img
                  src={preview}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  <ImageIcon className="h-8 w-8 text-neutral-400" />
                </div>
              )}

              {/* Remove Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                disabled={disabled}
                className={cn(
                  'absolute top-1 right-1 p-1.5 rounded-full',
                  'bg-black/60 text-white hover:bg-black/80',
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                  'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white'
                )}
                aria-label={`${file.name} entfernen`}
              >
                <X className="h-3 w-3" />
              </button>

              {/* File Name */}
              <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent">
                <p
                  className="text-xs text-white truncate"
                  title={file.name}
                >
                  {file.name}
                </p>
              </div>
            </div>
          );
        })}

        {/* Add More Button */}
        {files.length < maxFiles && (
          <button
            type="button"
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={disabled}
            className={cn(
              'aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors',
              isDragOver
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
                : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-600',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={t('workspace.addMoreImages')}
          >
            <Plus className="h-6 w-6 text-neutral-400" />
            <span className="text-xs text-neutral-500">{t('workspace.addMoreLabel')}</span>
          </button>
        )}
      </div>

      {/* Hidden Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        multiple={maxFiles > 1}
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled}
      />
    </div>
  );
}

export default UploadArea;
